// GENERATED by kanban-board-be `npm run contract:export`. Do not edit.
// Source of truth: kanban-board-be/src/contract. Change it there and re-export.

import { z } from "zod";

import {
  NotificationTypeSchema,
  ProjectStatusSchema,
  TaskPrioritySchema,
  UserRoleSchema,
  WorkflowStateTypeSchema,
} from "./entities.js";

/**
 * The write API.
 *
 * Every change to the board is one of these. There are no REST write endpoints
 * — a mutation travels over the same WebSocket as the deltas it will produce,
 * because the client has already applied it locally and what it needs back is
 * an acknowledgement and a version, not a response body.
 *
 * Three rules hold for all of them:
 *
 *   1. THE CLIENT MINTS THE ID. A create carries the uuid of the thing being
 *      created. This is not a convenience: the card has to render under the
 *      user's cursor before any network call happens, so it needs an identity
 *      before the server has heard of it. It also makes every create naturally
 *      idempotent — a retried create targets the same id.
 *
 *   2. ARGUMENTS ARE ABSOLUTE, NOT RELATIVE. `moveTask` carries the position
 *      to move to, never "move up one". Two people reordering the same column
 *      while offline would compose relative operations into nonsense; absolute
 *      values converge on last-writer-wins, which is both correct and what
 *      people expect from a board.
 *
 *   3. OMITTED IS NOT NULL. In an update, a field that is absent is unchanged
 *      and a field that is explicitly null is being cleared. Collapsing the two
 *      would make it impossible to unassign a task without also sending every
 *      other field — and then two concurrent edits to different fields of one
 *      card would overwrite each other.
 */

const uuid = z.uuid();

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------

export const CreateTaskSchema = z.object({
  id: uuid,
  projectId: uuid,
  title: z.string().min(1).max(500),
  description: z.string().max(100_000).optional(),
  stateId: uuid.optional(),
  assigneeId: uuid.nullish(),
  priority: TaskPrioritySchema.optional(),
  estimate: z.number().min(0).max(1000).nullish(),
  dueDate: z.iso.date().nullish(),
  parentId: uuid.nullish(),
  /** Omitted means append to the end of the column. */
  position: z.string().optional(),
  sourceSuggestionId: uuid.nullish(),
});

export const UpdateTaskSchema = z.object({
  id: uuid,
  title: z.string().min(1).max(500).optional(),
  description: z.string().max(100_000).nullish(),
  assigneeId: uuid.nullish(),
  priority: TaskPrioritySchema.optional(),
  estimate: z.number().min(0).max(1000).nullish(),
  dueDate: z.iso.date().nullish(),
});

/**
 * The drag. One row written, one delta broadcast, whatever the column holds.
 *
 * The client computes `position` from the fractional keys of the cards it is
 * dropping between, using the identical algorithm the server has. It is sent
 * rather than recomputed server-side because the client is the only party that
 * knows where the card was actually dropped.
 */
export const MoveTaskSchema = z.object({
  id: uuid,
  stateId: uuid,
  position: z.string().min(1).max(512),
  /**
   * Reserved for moving a card between projects. The server refuses a change
   * of project until that is built properly (FOS-450): it needs a new number,
   * detached sub-tasks and a removal for anyone who loses sight of the card.
   */
  projectId: uuid.optional(),
});

export const DeleteTaskSchema = z.object({ id: uuid });
export const RestoreTaskSchema = z.object({ id: uuid });

// ---------------------------------------------------------------------------
// Comments
// ---------------------------------------------------------------------------

export const CreateCommentSchema = z.object({
  id: uuid,
  taskId: uuid,
  body: z.string().min(1).max(50_000),
  parentId: uuid.nullish(),
});

export const UpdateCommentSchema = z.object({
  id: uuid,
  body: z.string().min(1).max(50_000),
});

export const DeleteCommentSchema = z.object({ id: uuid });

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

export const CreateLabelSchema = z.object({
  id: uuid,
  name: z.string().min(1).max(100),
  color: z.string().regex(/^[0-9a-fA-F]{6}$/),
});

export const UpdateLabelSchema = z.object({
  id: uuid,
  name: z.string().min(1).max(100).optional(),
  color: z
    .string()
    .regex(/^[0-9a-fA-F]{6}$/)
    .optional(),
});

export const DeleteLabelSchema = z.object({ id: uuid });

/** The join row id is minted by the client too, so the toggle is idempotent. */
export const AddTaskLabelSchema = z.object({ id: uuid, taskId: uuid, labelId: uuid });
export const RemoveTaskLabelSchema = z.object({ taskId: uuid, labelId: uuid });

// ---------------------------------------------------------------------------
// Projects and columns
// ---------------------------------------------------------------------------

export const CreateProjectSchema = z.object({
  id: uuid,
  name: z.string().min(1).max(200),
  key: z
    .string()
    .min(1)
    .max(8)
    .regex(/^[A-Z][A-Z0-9]*$/, "A project key is uppercase letters and digits, e.g. ENG"),
  description: z.string().max(100_000).nullish(),
  leadId: uuid.nullish(),
  startDate: z.iso.date().nullish(),
  targetDate: z.iso.date().nullish(),
  isPrivate: z.boolean().optional(),
  position: z.string().optional(),
});

export const UpdateProjectSchema = z.object({
  id: uuid,
  name: z.string().min(1).max(200).optional(),
  description: z.string().max(100_000).nullish(),
  status: ProjectStatusSchema.optional(),
  leadId: uuid.nullish(),
  startDate: z.iso.date().nullish(),
  targetDate: z.iso.date().nullish(),
  position: z.string().optional(),
});

export const DeleteProjectSchema = z.object({ id: uuid });

/** Who may see a private project. Refused on a public one, where it means nothing. */
export const AddProjectMemberSchema = z.object({ id: uuid, projectId: uuid, userId: uuid });
export const RemoveProjectMemberSchema = z.object({ projectId: uuid, userId: uuid });

export const CreateWorkflowStateSchema = z.object({
  id: uuid,
  projectId: uuid.nullish(),
  name: z.string().min(1).max(100),
  type: WorkflowStateTypeSchema,
  color: z
    .string()
    .regex(/^[0-9a-fA-F]{6}$/)
    .optional(),
  position: z.string().optional(),
});

export const UpdateWorkflowStateSchema = z.object({
  id: uuid,
  name: z.string().min(1).max(100).optional(),
  type: WorkflowStateTypeSchema.optional(),
  color: z
    .string()
    .regex(/^[0-9a-fA-F]{6}$/)
    .optional(),
  position: z.string().optional(),
});

export const DeleteWorkflowStateSchema = z.object({
  id: uuid,
  /** Cards in the deleted column move here. Required: cards cannot be orphaned. */
  moveTasksToStateId: uuid,
});

// ---------------------------------------------------------------------------
// People (admins only)
// ---------------------------------------------------------------------------

/**
 * Who someone is in the workspace. Admins only, and never on yourself: the
 * admin making the change is always still an admin afterwards, so a workspace
 * can never lock itself out by demoting or deactivating its last admin.
 *
 * Inviting is not here. An invitation hands back a sign-in link, which is a
 * credential, and a credential never travels on the sync stream that every
 * client replicates; it is a plain request to /api/admin/invitations.
 */
export const UpdateUserRoleSchema = z.object({ id: uuid, role: UserRoleSchema });

/** Ends every session at once; their tasks, comments and history stay theirs. */
export const DeactivateUserSchema = z.object({ id: uuid });
export const ReactivateUserSchema = z.object({ id: uuid });

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

export const MarkNotificationReadSchema = z.object({ id: uuid });

/** Everything up to and including `upToVersion`, so a mark-all is race-free. */
export const MarkAllNotificationsReadSchema = z.object({ upToVersion: z.number().int() });

/**
 * Turn one notification type on or off for one channel. `id` is used only for
 * the first change to that type; after that the existing row is updated.
 */
export const SetNotificationPreferenceSchema = z.object({
  id: uuid,
  type: NotificationTypeSchema,
  inApp: z.boolean().optional(),
  slack: z.boolean().optional(),
});

/** Follow or stop following a task. `id` as above. */
export const SetTaskSubscriptionSchema = z.object({
  id: uuid,
  taskId: uuid,
  subscribed: z.boolean(),
});

// ---------------------------------------------------------------------------

/**
 * The registry. A message whose `name` is not a key here is rejected before
 * anything is parsed, so adding a mutation is one entry in this object and a
 * handler — there is no route table to keep in step.
 */
export const MUTATIONS = {
  createTask: CreateTaskSchema,
  updateTask: UpdateTaskSchema,
  moveTask: MoveTaskSchema,
  deleteTask: DeleteTaskSchema,
  restoreTask: RestoreTaskSchema,

  createComment: CreateCommentSchema,
  updateComment: UpdateCommentSchema,
  deleteComment: DeleteCommentSchema,

  createLabel: CreateLabelSchema,
  updateLabel: UpdateLabelSchema,
  deleteLabel: DeleteLabelSchema,
  addTaskLabel: AddTaskLabelSchema,
  removeTaskLabel: RemoveTaskLabelSchema,

  createProject: CreateProjectSchema,
  updateProject: UpdateProjectSchema,
  deleteProject: DeleteProjectSchema,
  addProjectMember: AddProjectMemberSchema,
  removeProjectMember: RemoveProjectMemberSchema,

  createWorkflowState: CreateWorkflowStateSchema,
  updateWorkflowState: UpdateWorkflowStateSchema,
  deleteWorkflowState: DeleteWorkflowStateSchema,

  updateUserRole: UpdateUserRoleSchema,
  deactivateUser: DeactivateUserSchema,
  reactivateUser: ReactivateUserSchema,

  markNotificationRead: MarkNotificationReadSchema,
  markAllNotificationsRead: MarkAllNotificationsReadSchema,
  setNotificationPreference: SetNotificationPreferenceSchema,
  setTaskSubscription: SetTaskSubscriptionSchema,
} as const;

export type MutationName = keyof typeof MUTATIONS;
export type MutationArgs<N extends MutationName> = z.infer<(typeof MUTATIONS)[N]>;

export const MUTATION_NAMES = Object.keys(MUTATIONS) as MutationName[];

export function isMutationName(name: string): name is MutationName {
  return Object.prototype.hasOwnProperty.call(MUTATIONS, name);
}
